import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import { Button, Layout, PageTitle } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import {
  StepsCarousel,
  type CarouselStep,
} from "../components/StepsCarousel";
import { useStore } from "../store/globalState";
import { useSendStore } from "../store/useSendStore";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { getOperation } from "../services/api";
import { formatLabeledAmount } from "../utils";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { getEvents, safeCapture } from "../analytics";
import stepDepositTap from "../assets/cash_deposit_how_to/step-deposit-tap.svg?raw";
import step2 from "../assets/cash_deposit_how_to/step2.svg?raw";
import step3 from "../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";


const ILLUSTRATIONS = [stepDepositTap, step2, step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["1", "2", "3", "4", "5"] as const;

/**
 * Post-cash interstitial: tap Deposit on Transactions, go to a store, then
 * in-store steps. Illustrated carousel + optional store map; non-binding
 * guidance only.
 */
export const HowToDepositCashPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const quoteState = useSendStore((s) => s.quoteState);
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const [searchParams] = useSearchParams();
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
  } = colorConfig;
  const partnerColors = session.data?.partner?.theme?.colors as
    | Record<string, string>
    | undefined;
  const accentSolid = accentInkOnSurface(partnerColors, background, title);
  const inactiveColor = partnerColors?.inactived ?? footerColor;

  const linkId = session.data?.linkId;
  const operationId = searchParams.get("operationId");
  const [operationAmount, setOperationAmount] = useState<{
    totalCost: number;
    sourceCurrency: string;
  } | null>(null);

  useEffect(() => {
    if (quoteState?.totalCost != null || !linkId || !operationId) {
      return;
    }
    let cancelled = false;
    void getOperation(linkId, operationId)
      .then((op) => {
        if (!cancelled && op.totalCost != null) {
          setOperationAmount({
            totalCost: op.totalCost,
            sourceCurrency: op.sourceCurrency,
          });
        }
      })
      .catch(() => {
        // Keep the generic how-to amount if the operation cannot be loaded.
      });
    return () => {
      cancelled = true;
    };
  }, [quoteState?.totalCost, linkId, operationId]);

  const cashTotal =
    quoteState?.totalCost != null
      ? formatLabeledAmount(quoteState.totalCost, quoteState.sourceCurrency)
      : operationAmount
        ? formatLabeledAmount(
            operationAmount.totalCost,
            operationAmount.sourceCurrency,
          )
        : null;
  const stepAmount = cashTotal ?? t("howToDeposit.cashAmount");

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("how_to_deposit_cash"));
  }, []);

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        artClassName: key === "1" ? "cdh-slide-art--deposit-tap" : undefined,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToDeposit.steps.${key}.title`),
        body: t(`howToDeposit.steps.${key}.body`, { amount: stepAmount }),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToDeposit.steps.${key}.script`)
          : undefined,
      })),
    [t, stepAmount],
  );

  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <PageTitle
            highlight={t("howToDeposit.titleHighlight")}
            highlightColor={TITLE_COLOR}
            secudnary={t("howToDeposit.titleSecondary")}
            secudnaryColor={TITLE_COLOR}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToDeposit.title")}
          stepLabel={(position, total) =>
            t("howToDeposit.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToDeposit.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToDeposit.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={() => redirectTo(APP_ROUTES.STORES_MAP_PAGE)}
          >
            {t("howToDeposit.findStores")}
          </Button>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() =>
              redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true })
            }
          >
            {t("howToDeposit.cta")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
